import { useIsomorphicLayoutEffect } from '@cian/react-utils';
import { ReactNode, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getOffers, getSuggestionIds, type IInfiniteSearchResult } from 'shared/api/offers';
import { type IAppState } from 'shared/common/state';
import { type IOffer } from 'shared/offer';
import { type ICoordinatesOffers, requestSuggestionsDistances, setQsToUris } from 'shared/serp/state/suggestions';
import { useApplicationContext } from 'shared/utils/applicationContext';

import { addRecommendationsModelVersion } from './addRecommendationsModelVersion';
import { SuggestionsLayout } from './components/SuggestionsLayout';
import { CAT_PAGE_PATHNAME } from './constants';
import { getSuggestionIdsResult } from './getSuggestionIdsResult';
import { useSuggestionsOffersRenderer } from './useSuggestionsOffersRenderer';
import { useSuggestionsTracking } from './useSuggestionsTracking';
import { useUpdateSuggestionDistancesSeoText } from './useUpdateSuggestionDistancesSeoText';

const EMPTY_ADDITIONAL_SUGGESTIONS: IOffer[][] = [];
const EMPTY_RECOMMENDATIONS: IInfiniteSearchResult[] = [];
const INFINITE_SEARCH_ERROR_PREFIX = 'Failed get-infinite-search-result-desktop';

export function SuggestionsContainer() {
  const { httpApi, logger } = useApplicationContext();
  const dispatch = useDispatch();
  const offers = useSelector((state: IAppState) => state.results.offers);
  const suggestions = useSelector((state: IAppState) => state.results.suggestions);
  const jsonQuery = useSelector((state: IAppState) => state.results.jsonQuery);
  const queryString = useSelector((state: IAppState) => state.results.queryString);
  const fullUrl = useSelector((state: IAppState) => state.results.fullUrl);
  const liquiditySource = useSelector((state: IAppState) => state.liquiditySource);
  const searchRequestId = useSelector((state: IAppState) => state.searchRequestId);
  const searchVersionRef = useRef(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isRecommendationsPrefetching, setIsRecommendationsPrefetching] = useState(false);
  const [nextStepRecommendations, setNextStepRecommendations] = useState<IInfiniteSearchResult[]>([]);
  const [additionalSuggestions, setAdditionalSuggestions] = useState<IOffer[][]>([]);
  const latestSearchRequestIdRef = useRef(searchRequestId);
  const handledSearchVersionRef = useRef(-1);
  const isCurrentSearch = searchRequestId === latestSearchRequestIdRef.current;
  const currentAdditionalSuggestions = isCurrentSearch ? additionalSuggestions : EMPTY_ADDITIONAL_SUGGESTIONS;
  const currentNextStepRecommendations = isCurrentSearch ? nextStepRecommendations : EMPTY_RECOMMENDATIONS;
  const renderCards = useSuggestionsOffersRenderer();
  const handleTrackSuggestions = useSuggestionsTracking();
  const updateSuggestionDistancesSeoText = useUpdateSuggestionDistancesSeoText({ jsonQuery, searchVersionRef });
  const isSeoUrl = useMemo(() => !new URL(fullUrl).pathname.startsWith(CAT_PAGE_PATHNAME), [fullUrl]);
  const visibleSuggestionsNodes = useMemo<ReactNode[]>(() => {
    const initialSuggestions = suggestions ?? [];
    let previousSuggestionsCount = initialSuggestions.length;

    return [
      ...renderCards({
        pageNumber: 1,
        offers: initialSuggestions,
      }),
      ...currentAdditionalSuggestions.flatMap((suggestionsPage, suggestionsPageIndex) => {
        const suggestionsPageNodes = renderCards({
          previousSuggestionsCount,
          pageNumber: suggestionsPageIndex + 1,
          offers: suggestionsPage,
          suggestions: suggestionsPage,
        });

        previousSuggestionsCount += suggestionsPage.length;

        return suggestionsPageNodes;
      }),
    ];
  }, [currentAdditionalSuggestions, renderCards, suggestions]);
  const searchResultOfferIds = useMemo(() => offers.map(offer => offer.id), [offers]);
  const initialSuggestionOfferIds = useMemo(() => (suggestions || []).map(suggestion => suggestion.id), [suggestions]);
  const excludedOfferIds = useMemo(
    () =>
      Array.from(
        new Set([
          ...searchResultOfferIds,
          ...initialSuggestionOfferIds,
          ...currentAdditionalSuggestions.flat().map(suggestion => suggestion.id),
        ]),
      ),
    [currentAdditionalSuggestions, initialSuggestionOfferIds, searchResultOfferIds],
  );
  const loadInitialNextStepSuggestions = useCallback(
    async (searchVersion: number) => {
      try {
        const response = await getSuggestionIds(httpApi, {
          excludedOfferIds: Array.from(new Set([...searchResultOfferIds, ...initialSuggestionOfferIds])),
          jsonQuery,
          queryString,
          pageNumber: 1,
          searchResultOfferIds,
        });

        if (searchVersion !== searchVersionRef.current) {
          return;
        }

        setNextStepRecommendations(response);
      } catch (error) {
        if (searchVersion !== searchVersionRef.current) {
          return;
        }

        setNextStepRecommendations([]);
        const errorMessage = error instanceof Error ? `: ${error.message}` : '';
        const dataError = new Error(`${INFINITE_SEARCH_ERROR_PREFIX}${errorMessage}`);
        logger.error(dataError);
      }
    },
    [httpApi, initialSuggestionOfferIds, jsonQuery, logger, queryString, searchResultOfferIds],
  );

  const handleLoadMoreClick = useCallback(
    async (event: SyntheticEvent<HTMLAnchorElement>) => {
      event.preventDefault();

      const searchVersion = searchVersionRef.current;
      const nextStepOfferIds = currentNextStepRecommendations.map(recommendation => recommendation.itemId);
      const nextExcludedOfferIds = Array.from(new Set([...excludedOfferIds, ...nextStepOfferIds]));

      setIsLoading(true);
      setIsRecommendationsPrefetching(true);

      const requestNextStepData = () =>
        getSuggestionIdsResult(httpApi, {
          excludedOfferIds: nextExcludedOfferIds,
          jsonQuery,
          queryString,
          pageNumber: currentAdditionalSuggestions.length + 2,
          searchResultOfferIds,
        });
      const nextStepDataPromise = requestNextStepData();

      const nextStepOffersPromise = getOffers(httpApi, nextStepOfferIds, jsonQuery, liquiditySource);

      try {
        const nextStepOffers = await nextStepOffersPromise;

        if (searchVersion !== searchVersionRef.current) {
          return;
        }

        const { offersSerialized: offers, qsToUris } = nextStepOffers;

        dispatch(setQsToUris(qsToUris));

        const newSuggestions = addRecommendationsModelVersion(offers, currentNextStepRecommendations);

        setAdditionalSuggestions(prevState => [...prevState, newSuggestions]);

        handleTrackSuggestions(
          suggestions,
          currentAdditionalSuggestions.length + 1,
          [...(suggestions || []), ...currentAdditionalSuggestions.flat(), ...newSuggestions],
          newSuggestions,
        );

        setIsLoading(false);
        setNextStepRecommendations([]);

        const coordinatesOffers = currentAdditionalSuggestions
          .flat()
          .map(suggestion => {
            const { geo } = suggestion;

            if (!geo) {
              return null;
            }

            return {
              offerId: suggestion.id,
              coordinates: {
                lat: geo.coordinates.lat,
                lng: geo.coordinates.lng,
              },
            };
          })
          .filter(Boolean);

        if (coordinatesOffers.length > 0) {
          dispatch(requestSuggestionsDistances(coordinatesOffers as ICoordinatesOffers[]));
        }
      } catch (error) {
        if (searchVersion !== searchVersionRef.current) {
          return;
        }

        const errorMessage =
          error instanceof Error
            ? `
      error: ${error.message}`
            : '';
        const dataError = new Error(
          `Failed to get additional suggestions info,
      suggestions ids: ${nextStepOfferIds}
      excluded offer ids: ${nextExcludedOfferIds}${errorMessage}`,
        );
        logger.error(dataError);

        setIsLoading(false);
        setIsRecommendationsPrefetching(false);

        return;
      }

      let nextStepDataResult = await nextStepDataPromise;

      if (searchVersion !== searchVersionRef.current) {
        return;
      }

      if (!nextStepDataResult.ok && nextStepDataResult.shouldRetry) {
        nextStepDataResult = await requestNextStepData();
      }

      if (searchVersion !== searchVersionRef.current) {
        return;
      }

      setIsRecommendationsPrefetching(false);

      if (!nextStepDataResult.ok) {
        const errorMessage = nextStepDataResult.error instanceof Error ? `: ${nextStepDataResult.error.message}` : '';
        logger.error(new Error(`${INFINITE_SEARCH_ERROR_PREFIX}${errorMessage}`));

        return;
      }

      setNextStepRecommendations(nextStepDataResult.recommendations);
    },
    [
      currentAdditionalSuggestions,
      currentNextStepRecommendations,
      dispatch,
      excludedOfferIds,
      handleTrackSuggestions,
      httpApi,
      jsonQuery,
      liquiditySource,
      logger,
      queryString,
      searchResultOfferIds,
      suggestions,
    ],
  );

  useIsomorphicLayoutEffect(() => {
    if (searchRequestId === latestSearchRequestIdRef.current) {
      return;
    }

    latestSearchRequestIdRef.current = searchRequestId;
    searchVersionRef.current += 1;

    setAdditionalSuggestions([]);
    setNextStepRecommendations([]);
    setIsLoading(false);
    setIsRecommendationsPrefetching(false);
  }, [searchRequestId]);

  useEffect(() => {
    const searchVersion = searchVersionRef.current;

    if (searchRequestId !== latestSearchRequestIdRef.current || searchVersion === handledSearchVersionRef.current) {
      return;
    }

    handledSearchVersionRef.current = searchVersion;

    handleTrackSuggestions(suggestions, 0);
    void loadInitialNextStepSuggestions(searchVersion);

    if (suggestions?.length && isSeoUrl) {
      void updateSuggestionDistancesSeoText(searchVersion);
      dispatch(requestSuggestionsDistances());
    }
  }, [
    dispatch,
    handleTrackSuggestions,
    isSeoUrl,
    loadInitialNextStepSuggestions,
    searchRequestId,
    suggestions,
    updateSuggestionDistancesSeoText,
  ]);

  if (!suggestions?.length && !currentAdditionalSuggestions.length) {
    return null;
  }

  return (
    <SuggestionsLayout
      content={visibleSuggestionsNodes}
      hasMoreOffers={currentNextStepRecommendations.length > 0}
      isLoading={isCurrentSearch ? isLoading || isRecommendationsPrefetching : false}
      title={offers.length > 0 ? 'Дополнительные предложения по вашему запросу' : 'Похожие предложения'}
      onShowMoreClick={handleLoadMoreClick}
    />
  );
}
